import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CalendarDays, MapPin, ShieldCheck, Crosshair, FileText } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

export default function Guest() {
  const { code } = useParams();
  const [pass, setPass] = useState(null);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", email: "" });
  const [claimed, setClaimed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/guest/${code}`)
      .then(({ data }) => setPass(data))
      .catch(() => setError("This guest pass is invalid or has expired."));
  }, [code]);

  const register = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/guest/${code}/register`, form);
      setClaimed(true);
      toast.success("Guest spot confirmed!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const ev = pass?.event;

  return (
    <main className="relative overflow-hidden pt-16 min-h-screen flex items-center justify-center noise-overlay crosshair-grid px-5" data-testid="guest-page">
      <div className="w-full max-w-lg my-16">
        <div className="text-center mb-8">
          <LogoMark className="w-14 h-14 text-gold mx-auto" />
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-crimson mt-4">Guest Pass — {code?.toUpperCase()}</p>
        </div>

        {error && (
          <div className="border border-crimson/40 bg-card2/60 p-10 text-center" data-testid="guest-invalid">
            <p className="text-stone-300">{error}</p>
            <Link to="/" className="inline-block mt-6 border border-gold/40 font-condensed uppercase tracking-widest text-sm px-6 py-3 hover:border-gold hover:text-gold transition-colors">Back to Home</Link>
          </div>
        )}

        {!error && pass && (
          <div className="border border-gold/30 bg-card2/60 p-8 sm:p-10" data-testid="guest-pass-card">
            {ev ? (
              <>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-obsidian bg-gold px-2 py-0.5 font-semibold">{ev.type}</span>
                <h1 className="font-display text-2xl sm:text-3xl uppercase tracking-wide text-parchment mt-4">{ev.title}</h1>
                <div className="mt-4 space-y-2 text-sm text-stone-300">
                  <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-gold" />{new Date(`${ev.date}T00:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} — {ev.time}</p>
                  <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gold" />{ev.location}</p>
                </div>
                <p className="mt-4 text-sm text-stone-400 leading-relaxed">{ev.description}</p>
                <p className="mt-5 font-mono text-xs tracking-widest uppercase text-stone-500">
                  Hosted by member <span className="text-gold">{pass.member_name}</span>
                </p>
              </>
            ) : (
              <p className="text-stone-400">This pass references an event that has been removed.</p>
            )}

            {claimed || pass.status === "used" ? (
              <div className="mt-8 border border-gold/30 bg-obsidian/60 p-6 text-center" data-testid="guest-claimed">
                <ShieldCheck className="w-10 h-10 text-gold mx-auto" />
                <h2 className="font-display text-xl uppercase tracking-wider text-parchment mt-4">
                  {claimed ? "You're In" : "Pass Claimed"}
                </h2>
                <p className="text-sm text-stone-400 mt-3 leading-relaxed">
                  {claimed
                    ? `Welcome, ${form.name || pass.guest_name}. Your host will meet you at the gate — bring a photo ID, and eye/ear protection if you have it (loaners available).`
                    : `This pass was claimed by ${pass.guest_name || "a guest"}.`}
                </p>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/guest/${code}/waiver`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="waiver-download-button"
                  className="inline-flex items-center gap-2 mt-6 border border-gold/40 font-condensed uppercase tracking-widest text-sm px-6 py-3 hover:bg-gold hover:text-obsidian transition-colors"
                >
                  <FileText className="w-4 h-4" /> Safety Waiver (PDF)
                </a>
                <p className="font-mono text-[10px] text-stone-500 uppercase tracking-widest mt-3">Print and sign before arrival</p>
              </div>
            ) : ev ? (
              <form onSubmit={register} className="mt-8 space-y-5" data-testid="guest-register-form">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-stone-400 flex items-center gap-2">
                  <Crosshair className="w-4 h-4 text-gold" /> Claim your guest spot
                </p>
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Full Name</Label>
                  <Input data-testid="guest-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 bg-obsidian border-gold/25 text-parchment focus-visible:ring-gold rounded-none" />
                </div>
                <div>
                  <Label className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">Email</Label>
                  <Input data-testid="guest-email-input" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 bg-obsidian border-gold/25 text-parchment focus-visible:ring-gold rounded-none" />
                </div>
                <button type="submit" data-testid="guest-register-button" disabled={busy} className="w-full gold-sheen bg-gold text-obsidian font-condensed uppercase tracking-[0.22em] font-semibold py-3.5 hover:bg-gold-light transition-colors disabled:opacity-60">
                  {busy ? "Confirming..." : "Claim Guest Spot"}
                </button>
                <p className="font-mono text-[10px] text-stone-500 uppercase tracking-widest text-center">All guests sign a safety waiver on arrival</p>
              </form>
            ) : null}
          </div>
        )}

        {!error && !pass && <p className="text-center font-mono text-sm text-gold tracking-[0.3em] uppercase animate-pulse" data-testid="guest-loading">Validating pass...</p>}
      </div>
    </main>
  );
}
